import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SIMULATION_DURATION_MS,
  SIMULATION_MAX_COURSE_STEP_MS
} from '../constants/yearPlanSimulationConstants';
import {
  runYearPlanSimulation,
  type SimulationLogEntry,
  type YearPlanSimulationInput,
  type YearPlanSimulationResult
} from '../services/yearPlanSimulationService';

export type SimulationStatus = 'idle' | 'running' | 'done' | 'failed';

export interface UseYearPlanSimulationResult {
  status: SimulationStatus;
  // What the current run was started with; null before the first run and
  // after a reset.
  input: YearPlanSimulationInput | null;
  log: SimulationLogEntry[];
  result: YearPlanSimulationResult | null;
  run: (input: YearPlanSimulationInput) => void;
  reset: () => void;
}

// A course placed (or not) is held on screen for its share of the run's
// duration; the steps around the courses pass straight through.
const isCourseStep = (entry: SimulationLogEntry): boolean =>
  entry.kind === 'placed' || entry.kind === 'unplaced';

function courseStepDelayMs(input: YearPlanSimulationInput): number {
  const courseCount = input.selections.reduce((sum, s) => sum + s.courseCount, 0);
  return Math.min(SIMULATION_DURATION_MS / Math.max(courseCount, 1), SIMULATION_MAX_COURSE_STEP_MS);
}

/**
 * Runs the year plan simulation in the browser one step at a time, handing the
 * event loop back between steps so the spinner keeps turning and every step
 * reaches the screen as it is produced. A reset, a new run or an unmount
 * abandons the run in progress: its pending step finds itself superseded and
 * stops.
 */
export function useYearPlanSimulation(): UseYearPlanSimulationResult {
  const [status, setStatus] = useState<SimulationStatus>('idle');
  const [input, setInput] = useState<YearPlanSimulationInput | null>(null);
  const [log, setLog] = useState<SimulationLogEntry[]>([]);
  const [result, setResult] = useState<YearPlanSimulationResult | null>(null);
  const runIdRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback((): void => {
    runIdRef.current += 1;
    if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const run = useCallback(
    (runInput: YearPlanSimulationInput): void => {
      cancel();
      const runId = runIdRef.current;
      const generator = runYearPlanSimulation(runInput);
      const courseDelayMs = courseStepDelayMs(runInput);
      setStatus('running');
      setInput(runInput);
      setLog([]);
      setResult(null);

      const step = (): void => {
        if (runId !== runIdRef.current) return;
        try {
          const next = generator.next();
          if (next.done) {
            setResult(next.value);
            setStatus('done');
            return;
          }
          setLog((previous) => [...previous, next.value]);
          timeoutRef.current = setTimeout(step, isCourseStep(next.value) ? courseDelayMs : 0);
        } catch (error: unknown) {
          console.error('The year plan simulation failed', error);
          setStatus('failed');
        }
      };
      timeoutRef.current = setTimeout(step, 0);
    },
    [cancel]
  );

  const reset = useCallback((): void => {
    cancel();
    setStatus('idle');
    setInput(null);
    setLog([]);
    setResult(null);
  }, [cancel]);

  return { status, input, log, result, run, reset };
}
